import React, { forwardRef } from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import withTheme from '../styles/withTheme'
import Icon from '../Icon'
import useControlled from '../../utils/useControlled'

import './Alert.scss'
import RfButton from '../Rf-button'

const alertIconMap = {
	success: 'tick',
	warning: 'warning',
	error: 'warning'
}

const Alert = forwardRef(function Alert(props, ref) {
	const {
		children,
		className,
		fullWidth,
		onClose,
		role = 'alert',
		severity = 'success',
		visible: visibleProp,
		size = 'medium',
		...other
	} = props

	const icon = alertIconMap[severity]

	const [visible, setVisible] = useControlled({
		controlled: visibleProp,
		default: true
	})

	const handleClose = () => {
		setVisible(false)
		onClose()
	}

	return visible ? (
		<div
			ref={ref}
			className={clsx(
				'Vb-alert',
				severity && `Vb-alert--${severity}`,
				size && `Vb-alert--${size}`,
				fullWidth && `Vb-alert--full-width`,
				className
			)}
			role={role}
			{...other}
		>
			{icon && (
				<span className="Vb-alert__icon">
					<Icon size={size === 'small' ? 1 : 1.5}>{icon}</Icon>
				</span>
			)}
			<span className="Vb-alert__message">{children}</span>
			{typeof onClose === 'function' && (
				<RfButton icon="close-2" variant="black" IconProps={{ size: .75 }} onClick={handleClose} />
			)}
		</div>
	) : null
})

export default withTheme('Vb-alert')(Alert)

Alert.defaultProps = {
	fullWidth: false,
	role: 'alert',
	severity: 'success',
	size: 'medium'
}

Alert.propTypes = {
	/**
	 * The content of the component.
	 */
	children: PropTypes.node,
	/**
	 * @ignore
	 */
	className: PropTypes.string,
	/**
	 * If `true`, the alert is full width.
	 * @default false
	 */
	fullWidth: PropTypes.bool,
	/**
	 * Callback fired when the close button is clicked.
	 * When the calback is passed, a small close button appears in the component.
	 *
	 * @param {object} event The event source of the callback.
	 */
	onClose: PropTypes.func,
	/**
	 * The ARIA role attribute of the element.
	 * @default 'alert'
	 */
	role: PropTypes.string,
	/**
	 * The severity of the alert. This defines the color and icon used.
	 * @default 'success'
	 */
	severity: PropTypes.oneOf(['error', 'success', 'warning']),
	/**
	 * The severity of the alert. This defines the color and icon used.
	 * @default 'medium'
	 */
	size: PropTypes.oneOf(['medium', 'small']),
	/**
	 * If `true` or `false`, the alert visibility is controlled.
	 * @default undefined
	 */
	visible: PropTypes.bool
}
